import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import {
  defineMessages,
  FormattedMessage,
  injectIntl,
  intlShape
} from 'react-intl';
import { get } from 'lodash';
import RouterPropTypes from 'react-router-prop-types';
import { withRouter } from 'react-router';
import { Alert } from 'reactstrap';
import PropTypes from 'prop-types';

import { modelOf } from '../../../prop-types';
import PageTitle from '../../common/PageTitle';
import SEOTitle from '../../head/SEOTitle';
import AddressStore from '../../../store/AddressStore';
import ContentForState from '../../loader/ContentForState';
import Paths from '../../../types/Paths';
import UnexpectedError from '../../loader/UnexpectedError';
import AddressForm from '../../address/AddressForm';
import globalTranslations from '../../../i18n/globalTranslations';
import RouteService from '../../../services/RouteService';
import HelperNavigation from '../../common/HelperNavigation';
import generatePath from '../../../util/generatePath';

const messages = defineMessages({
  updateAddress: {
    id: 'myAccount.updateAddress',
    defaultMessage: 'Update Address'
  }
});

@observer
export class MyAccountAddress extends Component {
  state = {
    updateError: null,
    requestId: 0
  };

  componentDidMount() {
    this.maybeLoadAddress();
  }

  componentDidUpdate(prevProps) {
    const { match } = this.props;

    if (prevProps.match.params.id !== match.params.id) {
      this.maybeLoadAddress();
    }
  }

  maybeLoadAddress = () => {
    const { addressStore } = this.props;
    const address = this.getActiveAddress();
    if (!address) {
      addressStore.loadAddress(this.getActiveAddressId()).catch((e) => {
        console.error(e);
      });
    }
  };

  getBreadcrumbsPath = () => {
    const { intl, routeService } = this.props;
    return routeService.transformBreadCrumbs([
      {
        text: intl.formatMessage(globalTranslations.myAccount),
        url: Paths.MyAccount
      },
      {
        text: intl.formatMessage(globalTranslations.addressBookTitle),
        url: Paths.MyAccountAddressList
      },
      {
        text: intl.formatMessage(messages.updateAddress),
        url: generatePath(Paths.MyAccountAddress, {
          id: this.getActiveAddressId()
        })
      }
    ]);
  };

  getActiveAddressId = () => {
    const { match } = this.props;
    return match.params.id;
  };

  getActiveAddress = () => {
    const { addressStore } = this.props;
    return addressStore.addresses.get(this.getActiveAddressId());
  };

  updateAddress = (address) => {
    const { addressStore } = this.props;

    addressStore
      .updateAddress(this.getActiveAddressId(), address)
      .then(() => {
        this.setState({ updateError: null });
        addressStore.loadAddresses();
        this.navigateToAddressList();
      })
      .catch((error) =>
        this.setState({
          updateError: error,
          requestId: this.state.requestId + 1
        })
      );
  };

  navigateToAddressList() {
    const { history, routeService } = this.props;
    history.push(routeService.getPath(Paths.MyAccountAddressList));
  }

  renderAddressForm = () => {
    return (
      <AddressForm
        address={this.getActiveAddress()}
        onSubmit={this.updateAddress}
        buttonText={<FormattedMessage {...globalTranslations.update} />}
      />
    );
  };

  renderError = () => {
    const { updateError, requestId } = this.state;
    const errors = get(updateError, 'response.data.errors');
    return errors ? (
      <Alert
        className="MyAccountAddress__errors"
        color="danger"
        key={requestId}
      >
        {Object.values(updateError.response.data.errors).map((message) => (
          <div key={message}>{message}</div>
        ))}
      </Alert>
    ) : (
      <UnexpectedError />
    );
  };

  render() {
    const { addressStore } = this.props;
    const { updateError } = this.state;
    return (
      <div className="MyAccountAddress">
        <SEOTitle title={messages.updateAddress} />
        <HelperNavigation breadcrumbsPath={this.getBreadcrumbsPath()} />
        <PageTitle>
          <FormattedMessage {...messages.updateAddress} />
        </PageTitle>
        {updateError && this.renderError()}
        <ContentForState
          state={addressStore.addressStates.get(this.getActiveAddressId())}
          error={addressStore.lastError}
          forLoaded={() => this.renderAddressForm()}
        />
      </div>
    );
  }
}

MyAccountAddress.propTypes = {
  addressStore: modelOf(AddressStore).isRequired,
  history: RouterPropTypes.history.isRequired,
  intl: intlShape.isRequired,
  match: RouterPropTypes.match.isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired
};

export default injectIntl(
  inject('addressStore', 'routeService')(withRouter(MyAccountAddress))
);



// WEBPACK FOOTER //
// ./src/components/my-account/MyAccountAddress/index.jsx